import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue";
import { computed, defineAsyncComponent, watch } from "vue";
import { useContext } from "@/store/context";
import { useActivitySummaryStore } from "@/experiences/ai-summary/store/activity-summary.store";
import { storeToRefs } from "pinia";
import { SummarySourceType } from "@/types/summary";
// Async components
export default /*@__PURE__*/ _defineComponent({
    ...{
        name: "AiSummary"
    },
    __name: 'AiSummaryView',
    props: {
        sourceType: {},
        sourceId: {},
        responseCount: { default: 0 },
        activityId: { default: undefined },
        studyId: { default: undefined },
        participantFilterIds: { default: "" },
        hideStudyObjectiveBanner: { type: Boolean },
        isCrossMeeting: { type: Boolean },
        displayMode: { default: "accordion" },
        registerApi: { type: Function, default: undefined }
    },
    setup(__props) {
        const AiSummaryAccordion = defineAsyncComponent(() => import("@/experiences/ai-summary/modules/AiSummaryAccordion.vue"));
        const AiSummaryTaskCard = defineAsyncComponent(() => import("@/experiences/ai-summary/modules/AiSummaryTaskCard.vue"));
        const AiSummaryActivityAccordion = defineAsyncComponent(() => import("@/experiences/ai-summary/modules/AiSummaryActivityAccordion.vue"));
        const AiSummaryActivityCard = defineAsyncComponent(() => import("@/experiences/ai-summary/modules/AiSummaryActivityCard.vue"));
        // Setup
        const props = __props;
        const { isUnrestricted, currentStudyId } = storeToRefs(useContext());
        const isTask = computed(() => props.sourceType === SummarySourceType.Task);
        const isActivity = computed(() => props.sourceType === SummarySourceType.Activity);
        // Activity summary is the only consumer that needs both studyId and activityId in
        // the mount props. studyId is preferred from the explicit prop (passed by the JSP
        // mount glue) and falls back to the context store if absent.
        const resolvedStudyId = computed(() => props.studyId ?? currentStudyId.value);
        // Bind the store during setup so Pinia resolves THIS app's instance via inject. Bind synchronously
        // when studyId is already known; otherwise (studyId arriving async from the context store) watch
        // until it's ready, bind once, then dispose the watcher.
        let activityStore;
        const bindActivityStore = () => {
            activityStore = useActivitySummaryStore(resolvedStudyId.value, props.sourceId);
        };
        if (isActivity.value && resolvedStudyId.value != null) {
            bindActivityStore();
        }
        else if (isActivity.value) {
            const unwatch = watch(() => resolvedStudyId.value != null, (ready) => {
                if (!ready)
                    return;
                bindActivityStore();
                unwatch();
            });
        }
        const refreshSummary = () => activityStore?.refreshActivitySummary();
        props.registerApi?.({ refreshSummary });
        return (_ctx, _cache) => {
            return (isActivity.value)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    (__props.displayMode === 'card' && resolvedStudyId.value != null)
                        ? (_openBlock(), _createBlock(_unref(AiSummaryActivityCard), {
                            key: 0,
                            "study-id": resolvedStudyId.value,
                            "activity-id": __props.sourceId,
                            "is-unrestricted": _unref(isUnrestricted)
                        }, null, 8, ["study-id", "activity-id", "is-unrestricted"]))
                        : (resolvedStudyId.value != null)
                            ? (_openBlock(), _createBlock(_unref(AiSummaryActivityAccordion), {
                                key: 1,
                                "study-id": resolvedStudyId.value,
                                "activity-id": __props.sourceId,
                                "is-unrestricted": _unref(isUnrestricted)
                            }, null, 8, ["study-id", "activity-id", "is-unrestricted"]))
                            : _createCommentVNode("", true)
                ], 64))
                : (isTask.value)
                    ? (_openBlock(), _createBlock(_unref(AiSummaryTaskCard), {
                        key: 1,
                        "source-id": __props.sourceId,
                        "response-count": __props.responseCount,
                        "activity-id": __props.activityId,
                        "participant-filter-ids": __props.participantFilterIds,
                        "is-unrestricted": _unref(isUnrestricted)
                    }, null, 8, ["source-id", "response-count", "activity-id", "participant-filter-ids", "is-unrestricted"]))
                    : (_openBlock(), _createBlock(_unref(AiSummaryAccordion), {
                        key: 2,
                        "is-unrestricted": _unref(isUnrestricted),
                        "hide-study-objective-banner": __props.hideStudyObjectiveBanner,
                        "is-cross-meeting": __props.isCrossMeeting,
                        source: { id: __props.sourceId, type: __props.sourceType }
                    }, null, 8, ["is-unrestricted", "hide-study-objective-banner", "is-cross-meeting", "source"]));
        };
    }
});
